// Figma: geofence edit tools, the map's top-right corner (pin · edit/delete · entrance/delete).
// The entrance group is commented out until the entrance feature exists.
import * as React from "react";
import L from "leaflet";
import { Namespace, useT } from "@racoon/i18n";
import { IcLocation, IcPencil, IcRemove } from "@racoon/icons";
import { IconButton, cn } from "@racoon/ui";
import { MAP_CONTROL_CLASS } from "~/shared/ui/map-control";

export type GeofenceTool = "pin" | "edit" | "delete";

const GROUP = cn(
  MAP_CONTROL_CLASS,
  "flex h-8 divide-x divide-border-primary overflow-hidden rounded-lg",
);

const ToolButton = ({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <IconButton
    shape="segment"
    // The glyphs carry their own sizes (16, 18 and 24px in Figma).
    size={null}
    aria-label={label}
    title={label}
    aria-pressed={pressed}
    onClick={onClick}
  >
    {children}
  </IconButton>
);

export const GeofenceToolbar = ({
  tool,
  onToolChange,
}: {
  tool: GeofenceTool | null;
  onToolChange: (tool: GeofenceTool | null) => void;
}) => {
  const t = useT();
  const ref = React.useRef<HTMLDivElement>(null);

  // Inside the map container: a click here must not also land on the map.
  React.useEffect(() => {
    if (!ref.current) return;
    L.DomEvent.disableClickPropagation(ref.current);
    L.DomEvent.disableScrollPropagation(ref.current);
  }, []);

  const toggle = (next: GeofenceTool) => () => onToolChange(tool === next ? null : next);

  return (
    <div ref={ref} role="toolbar" className="absolute right-2 top-2 z-[1000] flex gap-2">
      <div className={cn(GROUP, "w-8")}>
        <ToolButton
          label={t(Namespace.PickupLocation, "geofenceTools.pin")}
          pressed={tool === "pin"}
          onClick={toggle("pin")}
        >
          <IcLocation className="size-6" />
        </ToolButton>
      </div>
      <div className={cn(GROUP, "w-16")}>
        <ToolButton
          label={t(Namespace.PickupLocation, "geofenceTools.edit")}
          pressed={tool === "edit"}
          onClick={toggle("edit")}
        >
          <IcPencil className="size-4" />
        </ToolButton>
        <ToolButton
          label={t(Namespace.PickupLocation, "geofenceTools.delete")}
          pressed={tool === "delete"}
          onClick={toggle("delete")}
        >
          {/* Figma's glyph is 12×14: IcRemove's art at an 18px frame. */}
          <IcRemove className="size-[18px]" />
        </ToolButton>
      </div>
      {/* Entrance tools, once the feature exists (IcSquareArrowDownRight from @racoon/icons):
      <div className={cn(GROUP, "w-16")}>
        <ToolButton label={t(Namespace.PickupLocation, "geofenceTools.addEntrance")} …>
          <IcSquareArrowDownRight className="size-6" />
        </ToolButton>
        <ToolButton label={t(Namespace.PickupLocation, "geofenceTools.deleteEntrance")} …>
          <IcRemove className="size-[18px]" />
        </ToolButton>
      </div> */}
    </div>
  );
};
